import { useState } from "react";
import { cn } from "@/lib/utils";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Pencil, Trash2, RotateCw, BookOpen, ImagePlus, Loader2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/common/PageHeader";
import { SearchBar } from "@/components/common/SearchBar";
import { DataTable, Column } from "@/components/tables/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ConfirmDeleteDialog } from "@/components/common/ConfirmDeleteDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useListState } from "@/hooks/useListState";
import { useGetSubjectsQuery, useCreateSubjectMutation, useUpdateSubjectMutation, useDeleteSubjectMutation, Subject } from "@/redux/slices/subjectApi";
import { useGetAcademicYearsQuery } from "@/redux/slices/academicYearApi";
import { useUploadImageMutation } from "@/redux/slices/uploadApi";
import { format } from "date-fns";
import { getFileUrl } from "@/lib/utils";

const schema = z.object({
  name: z.string().trim().min(2, "Name required").max(60),
  academicYearId: z.string().min(1, "Academic year required"),
  isActive: z.boolean(),
  icon: z.string().optional().nullable(),
  color: z.string().optional().nullable(),
});
type FormValues = z.infer<typeof schema>;

export default function SubjectsPage() {
  const list = useListState();
  const { data, isFetching, refetch } = useGetSubjectsQuery(list.queryParams());
  const { data: yearsData } = useGetAcademicYearsQuery({ limit: 100 });
  const [addSubject, addM] = useCreateSubjectMutation();
  const [updateSubject, updateM] = useUpdateSubjectMutation();
  const [deleteSubject, deleteM] = useDeleteSubjectMutation();
  const [uploadImage, uploadM] = useUploadImageMutation();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Subject | null>(null);
  const [toDelete, setToDelete] = useState<Subject | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", academicYearId: "", isActive: true, icon: "", color: "#3b82f6" },
  });

  const openCreate = () => {
    setEditing(null);
    form.reset({ name: "", academicYearId: "", isActive: true, icon: "", color: "#3b82f6" });
    setOpen(true);
  };
  const openEdit = (s: Subject) => {
    setEditing(s);
    form.reset({ 
      name: s.name, 
      academicYearId: s.academicYearId, 
      isActive: s.isActive, 
      icon: s.icon || "", 
      color: s.color || "#3b82f6" 
    });
    setOpen(true);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("image", file);

    try {
      const res = await uploadImage({ formData, folder: "subjects", subfolder: "icons" }).unwrap();
      form.setValue("icon", res.path);
      toast.success("Logo uploaded");
    } catch {
      toast.error("Upload failed");
    }
  };

  const onSubmit = async (v: FormValues) => {
    try {
      if (editing) {
        await updateSubject({ id: editing.id, body: v }).unwrap();
        toast.success("Subject updated");
      } else {
        await addSubject(v).unwrap();
        toast.success("Subject added");
      }
      setOpen(false);
    } catch {
      toast.error("Something went wrong");
    }
  };

  const columns: Column<Subject>[] = [
    { 
      key: "name", 
      header: "Subject Name", 
      render: (r) => (
        <div className="flex items-center gap-3">
          <div 
            className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-muted shadow-sm transition-transform hover:scale-105"
            style={{ borderColor: r.color ? `${r.color}40` : undefined }}
          >
            {r.icon ? (
              <img src={getFileUrl(r.icon)} alt={r.name} className="h-full w-full object-cover" />
            ) : (
              <BookOpen className="h-5 w-5 text-muted-foreground" />
            )}
          </div>
          <span className="font-medium text-foreground">{r.name}</span>
        </div>
      ) 
    },
    { key: "academicYear", header: "Academic Year", render: (r) => <span className="text-muted-foreground">{r.academicYear?.name || "—"}</span> },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.isActive ? "active" : "inactive"} /> },
    { key: "createdAt", header: "Created", render: (r) => format(new Date(r.createdAt), "dd MMM yyyy") },
  ];

  return (
    <div className="min-h-screen bg-muted/20">
      <PageHeader
        title="Subjects"
        description="Manage study subjects and educational courses"
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching}>
              <RotateCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
            </Button>
            <Button onClick={openCreate} className="shadow-lg shadow-primary/20">
              <Plus className="mr-1.5 h-4 w-4" /> Add Subject
            </Button>
          </div>
        }
      />
      <div className="space-y-6 p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col gap-4 rounded-2xl border bg-card p-4 shadow-sm lg:flex-row lg:items-center">
          <SearchBar 
            value={list.search} 
            onChange={list.setSearch} 
            placeholder="Search subjects..." 
            className="flex-1" 
          />
          <div className="flex flex-wrap items-center gap-3">
            <Select
              value={list.filters.academicYearId ?? "all"}
              onValueChange={(v) => list.setFilter("academicYearId", v === "all" ? undefined : v)}
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="All Academic Years" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Academic Years</SelectItem>
                {yearsData?.data?.map(y => (
                  <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={list.filters.isActive ?? "all"}
              onValueChange={(v) => list.setFilter("isActive", v === "all" ? undefined : v)}
            >
              <SelectTrigger className="w-full sm:w-[140px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="true">Active Only</SelectItem>
                <SelectItem value="false">Inactive Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DataTable
          columns={columns}
          rows={data?.data ?? []}
          loading={isFetching}
          total={data?.total ?? 0}
          page={list.page}
          limit={list.limit}
          onPageChange={list.setPage}
          onLimitChange={list.setLimit}
          actions={(row) => (
            <div className="flex justify-end gap-1">
              <Button size="icon" variant="ghost" onClick={() => openEdit(row)}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="ghost" onClick={() => setToDelete(row)} className="text-destructive">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg shadow-xl border-none p-0 overflow-hidden rounded-xl flex flex-col max-h-[85vh]">
          <DialogHeader className="p-6 pb-4 bg-primary/5 text-left shrink-0">
            <DialogTitle className="text-xl font-bold">{editing ? "Edit Subject Details" : "Create New Subject"}</DialogTitle>
            <DialogDescription className="font-medium text-sm text-muted-foreground">Configure course details and branding for this subject.</DialogDescription>
          </DialogHeader>
          <div className="px-6 py-4 flex-1 overflow-y-auto">
            <form id="subject-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-semibold text-foreground">Subject Name</Label>
                  <Input id="name" className="h-11 shadow-sm" placeholder="e.g. Kayachikitsa" {...form.register("name")} />
                  {form.formState.errors.name && (
                    <p className="text-xs font-medium text-destructive">{form.formState.errors.name.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-foreground">Academic Year</Label>
                  <Select
                    value={form.watch("academicYearId") || undefined}
                    onValueChange={(v) => form.setValue("academicYearId", v)}
                  >
                    <SelectTrigger className="h-11 shadow-sm">
                      <SelectValue placeholder="Select Year" />
                    </SelectTrigger>
                    <SelectContent>
                      {yearsData?.data?.map(y => (
                        <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {form.formState.errors.academicYearId && (
                    <p className="text-xs font-medium text-destructive">{form.formState.errors.academicYearId.message}</p>
                  )}
                </div>
              </div>

              <div className="space-y-3 rounded-xl border bg-muted/10 p-5 shadow-sm">
                <Label className="text-xs font-bold text-primary uppercase tracking-wider">Subject Branding</Label>
                <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
                  <div className="relative group flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-primary/20 bg-background shadow-sm transition-all hover:border-primary/50">
                    {form.watch("icon") ? (
                      <img src={getFileUrl(form.watch("icon")!)} className="h-full w-full object-cover" />
                    ) : (
                      <ImagePlus className="h-6 w-6 text-muted-foreground/40" />
                    )}
                    {uploadM.isLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                        <Loader2 className="h-6 w-6 animate-spin text-white" />
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="absolute inset-0 cursor-pointer opacity-0"
                      onChange={handleImageUpload}
                      disabled={uploadM.isLoading}
                    />
                  </div>
                  <div className="flex-1 space-y-2 mt-1">
                    <Label htmlFor="color" className="text-xs font-semibold text-foreground">Theme Color</Label>
                    <div className="flex items-center gap-2">
                      <div className="h-10 w-12 overflow-hidden rounded-md border shadow-sm shrink-0">
                        <input 
                          id="color-picker" 
                          type="color" 
                          className="h-12 w-14 -ml-1 -mt-1 cursor-pointer" 
                          value={form.watch("color") || "#3b82f6"} 
                          onChange={(e) => form.setValue("color", e.target.value)}
                        />
                      </div>
                      <Input 
                        id="color-text"
                        className="h-10 font-mono text-xs shadow-sm flex-1" 
                        placeholder="#3b82f6" 
                        {...form.register("color")} 
                      />
                    </div>
                    <p className="text-[10px] text-muted-foreground leading-snug">Choose a color or enter hex code.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-foreground">Publication Status</Label>
                <Select
                  value={form.watch("isActive") ? "true" : "false"}
                  onValueChange={(v) => form.setValue("isActive", v === "true")}
                >
                  <SelectTrigger className="h-11 shadow-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Active (Students can enroll)</SelectItem>
                    <SelectItem value="false">Inactive (Maintenance mode)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </form>
          </div>
          <DialogFooter className="border-t p-6 bg-muted/5 shrink-0">
            <Button variant="ghost" onClick={() => setOpen(false)} className="h-10 px-4 text-sm font-medium">Cancel</Button>
            <Button form="subject-form" type="submit" disabled={addM.isLoading || updateM.isLoading} className="h-10 px-6 rounded-md text-sm font-medium shadow-md transition-all">
              {editing ? "Update Subject" : "Create Subject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        onConfirm={async () => {
          if (!toDelete) return;
          await deleteSubject(toDelete.id).unwrap();
          toast.success("Subject permanently removed");
          setToDelete(null);
        }}
        loading={deleteM.isLoading}
        title="Delete subject?"
        description={`"${toDelete?.name}" will be removed along with all its associated chapter tests and video lectures.`}
      />
    </div>
  );
}
